import React, { useState } from 'react';
import { Eye, EyeSlash } from '@phosphor-icons/react';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  autoFocus?: boolean;
  /** Style applied to the <input> (visual: bg/border/padding/etc.). */
  style?: React.CSSProperties;
  /** Style applied to the wrapper (layout: flex/width) — needed inside flex rows. */
  wrapperStyle?: React.CSSProperties;
}

/**
 * Password field with an in-field show/hide eye toggle (P3).
 * The toggle is type="button" so it never submits the form; it's keyboard- and
 * screen-reader-accessible via aria-label / aria-pressed.
 */
export const PasswordInput: React.FC<Props> = ({
  value, onChange, style, wrapperStyle, ...rest
}) => {
  const [visible, setVisible] = useState(false);
  return (
    <div style={{ position: 'relative', width: '100%', ...wrapperStyle }}>
      <input
        {...rest}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...style, paddingRight: 38, width: '100%' }}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        title={visible ? 'Hide password' : 'Show password'}
        style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)',
          display: 'flex', alignItems: 'center', padding: 2,
        }}
      >
        {visible ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
      </button>
    </div>
  );
};

export default PasswordInput;
